export const RATIOS = {
  S: 1,
  r: 16 / 15,
  R: 9 / 8,
  g: 6 / 5,
  G: 5 / 4,
  M: 4 / 3,
  m: 45 / 32,
  P: 3 / 2,
  d: 8 / 5,
  D: 5 / 3,
  n: 16 / 9,
  N: 15 / 8,
};

export const SWARA_NAMES = {
  S: 'Sa',
  r: 'Re komal',
  R: 'Re',
  g: 'Ga komal',
  G: 'Ga',
  M: 'Ma',
  m: 'Ma tivra',
  P: 'Pa',
  d: 'Dha komal',
  D: 'Dha',
  n: 'Ni komal',
  N: 'Ni',
};

export const DEVANAGARI = {
  S: 'सा',
  r: 'रे॒',
  R: 'रे',
  g: 'ग॒',
  G: 'ग',
  M: 'म',
  m: 'म॑',
  P: 'प',
  d: 'ध॒',
  D: 'ध',
  n: 'नि॒',
  N: 'नि',
};

export const RAGAS = {
  yaman: {
    id: 'yaman',
    name: 'Yaman',
    swaras: ['S', 'R', 'G', 'm', 'P', 'D', 'N'],
  },
  bilawal: {
    id: 'bilawal',
    name: 'Bilawal',
    swaras: ['S', 'R', 'G', 'M', 'P', 'D', 'N'],
  },
  bhairav: {
    id: 'bhairav',
    name: 'Bhairav',
    swaras: ['S', 'r', 'G', 'M', 'P', 'd', 'N'],
  },
  bhairavi: {
    id: 'bhairavi',
    name: 'Bhairavi',
    swaras: ['S', 'r', 'g', 'M', 'P', 'd', 'n'],
  },
  kafi: {
    id: 'kafi',
    name: 'Kafi',
    swaras: ['S', 'R', 'g', 'M', 'P', 'D', 'n'],
  },
  desh: {
    id: 'desh',
    name: 'Desh',
    swaras: ['S', 'R', 'M', 'P', 'N'],
  },
  todi: {
    id: 'todi',
    name: 'Todi',
    swaras: ['S', 'r', 'g', 'm', 'P', 'd', 'N'],
  },
  kedar: {
    id: 'kedar',
    name: 'Kedar',
    swaras: ['S', 'M', 'm', 'P', 'D'],
  },
};

export const SA_OPTIONS = [
  { label: 'C 261', hz: 261.63 },
  { label: 'C# 277', hz: 277.18 },
  { label: 'D 294', hz: 293.66 },
  { label: 'D# 311', hz: 311.13 },
  { label: 'E 330', hz: 329.63 },
  { label: 'F 349', hz: 349.23 },
];

const KEY_ORDER = ['S', 'R', 'G', 'M', 'P', 'D', 'N', 'S2'];

function makeNoiseBuffer(ctx) {
  const len = ctx.sampleRate * 2;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

function createPeriodicWave(ctx) {
  const real = new Float32Array([0, 1, 0.55, 0.38, 0.2, 0.17, 0.1, 0.13, 0.06, 0.08, 0.04]);
  const imag = new Float32Array(real.length);
  return ctx.createPeriodicWave(real, imag);
}

class Voice {
  constructor(ctx, dest, wave, noiseBuf) {
    this.ctx = ctx;
    this.active = false;
    this.freq = 220;

    this.out = ctx.createGain();
    this.out.gain.value = 0;
    this.out.connect(dest);

    this.body = ctx.createBiquadFilter();
    this.body.type = 'lowpass';
    this.body.Q.value = 0.7;
    this.body.connect(this.out);

    this.f1 = ctx.createBiquadFilter();
    this.f1.type = 'bandpass';
    this.f1.Q.value = 4.8;
    this.f1.connect(this.body);

    this.f2 = ctx.createBiquadFilter();
    this.f2.type = 'bandpass';
    this.f2.Q.value = 5.4;
    this.f2.connect(this.body);

    this.f3 = ctx.createBiquadFilter();
    this.f3.type = 'peaking';
    this.f3.Q.value = 2.2;
    this.f3.gain.value = 4.5;
    this.f3.connect(this.body);

    this.oscA = ctx.createOscillator();
    this.oscB = ctx.createOscillator();
    this.oscA.setPeriodicWave(wave);
    this.oscB.setPeriodicWave(wave);
    this.oscB.detune.value = 7;

    this.oscGain = ctx.createGain();
    this.oscGain.gain.value = 0.22;
    this.oscA.connect(this.oscGain);
    this.oscB.connect(this.oscGain);
    this.oscGain.connect(this.f1);
    this.oscGain.connect(this.f2);
    this.oscGain.connect(this.f3);

    this.noise = ctx.createBufferSource();
    this.noise.buffer = noiseBuf;
    this.noise.loop = true;
    this.noiseFilter = ctx.createBiquadFilter();
    this.noiseFilter.type = 'bandpass';
    this.noiseFilter.Q.value = 1.1;
    this.noiseGain = ctx.createGain();
    this.noiseGain.gain.value = 0.018;
    this.noise.connect(this.noiseFilter);
    this.noiseFilter.connect(this.noiseGain);
    this.noiseGain.connect(this.body);

    this.lfo = ctx.createOscillator();
    this.lfo.frequency.value = 5.3;
    this.lfoGain = ctx.createGain();
    this.lfoGain.gain.value = 5;
    this.lfo.connect(this.lfoGain);
    this.lfoGain.connect(this.oscA.detune);
    this.lfoGain.connect(this.oscB.detune);

    this.oscA.start();
    this.oscB.start();
    this.noise.start();
    this.lfo.start();
  }

  start(freq, pressure = 0.7) {
    const t = this.ctx.currentTime;
    this.freq = freq;
    this.active = true;
    this.oscA.frequency.setTargetAtTime(freq, t, 0.012);
    this.oscB.frequency.setTargetAtTime(freq, t, 0.012);
    this.noiseFilter.frequency.setTargetAtTime(freq * 2.1, t, 0.02);
    this.f1.frequency.setTargetAtTime(Math.min(780, freq * 2.35), t, 0.03);
    this.f2.frequency.setTargetAtTime(Math.min(1800, freq * 5.1), t, 0.03);
    this.f3.frequency.setTargetAtTime(Math.min(2600, freq * 8.4), t, 0.03);
    this.body.frequency.setTargetAtTime(1800 + pressure * 2200, t, 0.04);
    this.out.gain.cancelScheduledValues(t);
    this.out.gain.setValueAtTime(Math.max(this.out.gain.value, 0.0001), t);
    this.out.gain.exponentialRampToValueAtTime(0.0001, t + 0.01);
    this.out.gain.exponentialRampToValueAtTime(0.22 + pressure * 0.18, t + 0.07);
    this.noiseGain.gain.setTargetAtTime(0.012 + pressure * 0.03, t, 0.04);
    this.lfo.frequency.setTargetAtTime(4.6 + pressure * 1.4, t, 0.08);
  }

  glide(freq, pressure = 0.7) {
    const t = this.ctx.currentTime;
    this.freq = freq;
    this.oscA.frequency.setTargetAtTime(freq, t, 0.045);
    this.oscB.frequency.setTargetAtTime(freq, t, 0.045);
    this.noiseFilter.frequency.setTargetAtTime(freq * 2.1, t, 0.05);
    this.f1.frequency.setTargetAtTime(Math.min(780, freq * 2.35), t, 0.05);
    this.f2.frequency.setTargetAtTime(Math.min(1800, freq * 5.1), t, 0.05);
    this.body.frequency.setTargetAtTime(1800 + pressure * 2200, t, 0.06);
    this.out.gain.setTargetAtTime(0.2 + pressure * 0.2, t, 0.05);
  }

  stop() {
    const t = this.ctx.currentTime;
    this.active = false;
    this.out.gain.cancelScheduledValues(t);
    this.out.gain.setTargetAtTime(0.0001, t, 0.08);
  }
}

export class SarangiAudio {
  constructor() {
    this.ctx = null;
    this.sa = 277.18;
    this.raga = RAGAS.yaman;
    this.droneOn = true;
    this.ready = false;
    this.voices = [];
    this.held = new Map();
    this.droneNodes = [];
    this.sympathetic = [];
    this.master = null;
    this.onNote = null;
  }

  async unlock() {
    if (this.ready) {
      if (this.ctx.state === 'suspended') await this.ctx.resume();
      return;
    }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = 0.9;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.knee.value = 18;
    comp.ratio.value = 2.4;
    comp.attack.value = 0.01;
    comp.release.value = 0.18;
    this.master.connect(comp);
    comp.connect(ctx.destination);

    const wave = createPeriodicWave(ctx);
    const noise = makeNoiseBuffer(ctx);
    this.voices = Array.from({ length: 4 }, () => new Voice(ctx, this.master, wave, noise));
    this._buildSympathetic();
    this._buildDrone();
    this.ready = true;
    if (this.droneOn) this.startDrone();
    if (ctx.state === 'suspended') await ctx.resume();
  }

  setSa(hz) {
    this.sa = hz;
    if (this.ready && this.droneOn) {
      this.stopDrone();
      this._buildDrone();
      this.startDrone();
    }
    if (this.ready) this._tuneSympathetic();
  }

  setRaga(id) {
    this.raga = RAGAS[id] || RAGAS.yaman;
    if (this.ready) this._tuneSympathetic();
  }

  setDrone(on) {
    this.droneOn = on;
    if (!this.ready) return;
    if (on) this.startDrone();
    else this.stopDrone();
  }

  freqFor(token, octave = 0) {
    if (token === 'S2') return this.sa * 2 * 2 ** octave;
    const ratio = RATIOS[token];
    return this.sa * ratio * 2 ** octave;
  }

  keyboardMap() {
    const s = this.raga.swaras;
    const map = {};
    const keys = ['KeyA', 'KeyS', 'KeyD', 'KeyF', 'KeyG', 'KeyH', 'KeyJ', 'KeyK'];
    keys.forEach((code, i) => {
      if (i === 7) map[code] = { token: 'S2', octave: 0, swara: 'S' };
      else map[code] = { token: s[i] || s[s.length - 1], octave: 0, swara: s[i] };
    });
    return map;
  }

  displaySwaras() {
    return this.raga.swaras.concat(['S2']);
  }

  snapFreq(freq) {
    const candidates = [];
    for (let oct = -1; oct <= 2; oct++) {
      for (const sw of this.raga.swaras) {
        candidates.push(this.sa * RATIOS[sw] * 2 ** oct);
      }
      candidates.push(this.sa * 2 ** (oct + 1));
    }
    let best = candidates[0];
    let bestC = Infinity;
    for (const c of candidates) {
      const cents = Math.abs(1200 * Math.log2(freq / c));
      if (cents < bestC) {
        bestC = cents;
        best = c;
      }
    }
    return best;
  }

  _nearestSwara(freq) {
    let best = { token: 'S', name: 'Sa', deva: 'सा', oct: 0 };
    let bestC = Infinity;
    const tokens = [...this.raga.swaras, 'S'];
    for (let oct = -1; oct <= 2; oct++) {
      for (const token of tokens) {
        const f = this.sa * RATIOS[token === 'S' && oct > 0 ? 'S' : token] * 2 ** oct;
        const cents = Math.abs(1200 * Math.log2(freq / f));
        if (cents < bestC) {
          bestC = cents;
          best = {
            token,
            name: SWARA_NAMES[token],
            deva: DEVANAGARI[token],
            oct,
          };
        }
      }
    }
    return best;
  }

  noteOn(id, freq, pressure = 0.75) {
    if (!this.ready) return;
    let voice = this.held.get(id);
    if (!voice) {
      voice = this.voices.find((v) => !v.active) || this.voices[0];
      this.held.set(id, voice);
      voice.start(freq, pressure);
    } else {
      voice.glide(freq, pressure);
    }
    this._exciteSympathetic(freq);
    const info = this._nearestSwara(freq);
    this.onNote?.(info, true);
  }

  noteOff(id) {
    const voice = this.held.get(id);
    if (voice) {
      voice.stop();
      this.held.delete(id);
    }
    if (this.held.size === 0) this.onNote?.(null, false);
  }

  stopAll() {
    for (const id of [...this.held.keys()]) this.noteOff(id);
  }

  _buildSympathetic() {
    this.sympathetic.forEach((n) => n.stop?.());
    this.sympathetic = [];
    const ctx = this.ctx;
    const bus = ctx.createGain();
    bus.gain.value = 0.045;
    bus.connect(this.master);
    for (let i = 0; i < 16; i++) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      g.gain.value = 0;
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.Q.value = 18;
      osc.type = 'sine';
      osc.connect(f);
      f.connect(g);
      g.connect(bus);
      osc.start();
      this.sympathetic.push({ osc, g, f, freq: 220 });
    }
    this._tuneSympathetic();
  }

  _tuneSympathetic() {
    if (!this.sympathetic.length) return;
    const freqs = [];
    for (const sw of this.raga.swaras) freqs.push(this.sa * RATIOS[sw]);
    for (let i = 0; i < 12; i++) freqs.push(this.sa * 2 ** (i / 12));
    this.sympathetic.forEach((node, i) => {
      const freq = freqs[i % freqs.length] * (i > 10 ? 2 : 1);
      node.freq = freq;
      node.osc.frequency.value = freq;
      node.f.frequency.value = freq;
    });
  }

  _exciteSympathetic(freq) {
    const t = this.ctx.currentTime;
    for (const node of this.sympathetic) {
      const cents = Math.abs(1200 * Math.log2(freq / node.freq));
      const oct = Math.abs(1200 * Math.log2(freq / (node.freq * 2)));
      const hit = Math.min(cents, oct);
      if (hit < 45) {
        const amp = 0.08 * (1 - hit / 45);
        node.g.gain.cancelScheduledValues(t);
        node.g.gain.setTargetAtTime(amp, t, 0.03);
        node.g.gain.setTargetAtTime(0.0001, t + 0.12, 0.45);
      }
    }
  }

  _buildDrone() {
    this.droneNodes = [];
    const ctx = this.ctx;
    const bus = ctx.createGain();
    bus.gain.value = 0;
    this.droneBus = bus;
    bus.connect(this.master);
    const notes = [this.sa * 0.5, this.sa * 0.75, this.sa, this.sa * 2];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const wave = createPeriodicWave(ctx);
      osc.setPeriodicWave(wave);
      osc.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.value = i === 2 ? 0.08 : 0.055;
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = 1400;
      osc.connect(f);
      f.connect(g);
      g.connect(bus);
      osc.start();
      this.droneNodes.push({ osc, g });
    });
  }

  startDrone() {
    if (!this.droneBus) return;
    const t = this.ctx.currentTime;
    this.droneBus.gain.setTargetAtTime(0.55, t, 0.4);
  }

  stopDrone() {
    if (!this.droneBus) return;
    const t = this.ctx.currentTime;
    this.droneBus.gain.setTargetAtTime(0, t, 0.25);
  }
}

export { KEY_ORDER };
